import React, { useState, useEffect, useCallback } from 'react';
import { adminUsersService, AdminUserDetail } from '../../services/admin.users.service';
import { UserRole } from '../../types';
import { useToast } from '../../context/ToastContext';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import {
  Search,
  Filter,
  ShieldCheck,
  UserX,
  UserCheck,
  Eye,
  Mail,
  Clock,
  ChevronRight,
  X,
  CreditCard,
  Lock,
  Loader2
} from 'lucide-react';

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<AdminUserDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);

  const { showToast } = useToast();

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    const response = await adminUsersService.listUsers({
      page,
      search: searchQuery,
      role: roleFilter,
      status: statusFilter
    });

    if (response.success && response.data) {
      setUsers(response.data);
    } else {
      showToast(response.message || 'Failed to fetch entities', 'error');
    }
    setIsLoading(false);
  }, [page, searchQuery, roleFilter, statusFilter, showToast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 500); // Debounce search
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const handleToggleStatus = async (userId: string, currentStatus: string) => {
    const response = currentStatus === 'ACTIVE'
      ? await adminUsersService.suspendUser(userId)
      : await adminUsersService.activateUser(userId);

    if (response.success) {
      showToast(response.message || 'Protocol updated', 'success');
      fetchUsers(); // Refresh list
      if (selectedUserId === userId) fetchDetail(userId); // Refresh modal if open
    } else {
      showToast(response.message || 'Update failed', 'error');
    }
  };

  const fetchDetail = async (id: string) => {
    setIsDetailLoading(true);
    setRevealedPassword(null);
    const response = await adminUsersService.getUserDetail(id);
    if (response.success && response.data) {
      setUserDetail(response.data);
    } else {
      showToast(response.message || 'Inspection failed', 'error');
      setSelectedUserId(null);
    }
    setIsDetailLoading(false);
  };

  const handleShowPassword = async (id: string) => {
    setIsPasswordLoading(true);
    const response = await adminUsersService.showPassword(id);
    if (response.success && response.password) {
      setRevealedPassword(response.password);
    } else {
      showToast(response.message || 'Decryption failed', 'error');
    }
    setIsPasswordLoading(false);
  };

  const getKycBadge = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'PENDING': return 'bg-brand-primary/10 text-brand-primary border-brand-primary/20';
      case 'REJECTED': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-brand-muted/10 text-brand-muted border-brand-border';
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">

      {/* 1. Page Header */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase">Entity Management</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70">Global User Ledger & Compliance Control</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-brand-primary/10 border border-brand-primary/20 rounded-xl">
          <ShieldCheck size={16} className="text-brand-primary" />
          <span className="text-[10px] font-black text-brand-primary uppercase tracking-widest">{users.length} Records Found</span>
        </div>
      </section>

      {/* 2. Search & Filtration Bar */}
      <section className="bg-brand-card border border-brand-border p-4 rounded-3xl flex flex-col lg:flex-row gap-4 items-end lg:items-center">
        <div className="w-full lg:flex-1 relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted group-focus-within:text-brand-primary transition-colors" size={18} />
          <input
            type="text"
            placeholder="Search by Identity or Secure Email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-brand-dark/50 border border-brand-border rounded-xl pl-12 pr-4 py-3 text-sm text-white focus:outline-none focus:border-brand-primary transition-all"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full lg:w-auto">
          <Select
            options={[
              { value: 'ALL', label: 'All Roles' },
              { value: UserRole.ADMIN, label: 'Admins' },
              { value: UserRole.USER, label: 'Traders' }
            ]}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="!text-[10px] !py-2.5 !px-3"
          />
          <Select
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'SUSPENDED', label: 'Suspended' }
            ]}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="!text-[10px] !py-2.5 !px-3"
          />
        </div>
      </section>

      {/* 3. User List Viewports */}
      <div className="min-h-[400px] relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-brand-dark/20 backdrop-blur-[2px] rounded-3xl">
            <Loader2 className="animate-spin text-brand-primary" size={40} />
          </div>
        )}

        {users.length === 0 && !isLoading ? (
          <div className="py-20 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-full bg-brand-border/30 flex items-center justify-center text-brand-muted mb-4">
              <Filter size={32} />
            </div>
            <h3 className="text-white font-bold text-lg mb-1 uppercase tracking-tight">No Entities Found</h3>
            <p className="text-brand-muted text-sm max-w-[240px]">No system participants match your current filtration protocol.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-brand-dark/40 border-b border-brand-border">
                    <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Participant Identity</th>
                    <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Authority</th>
                    <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Status</th>
                    <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">KYC Vault</th>
                    <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-brand-primary/5 transition-colors group">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-brand-dark border border-brand-border flex items-center justify-center text-xs font-bold text-brand-muted group-hover:text-brand-primary transition-colors">
                            {user.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white leading-none">{user.name}</p>
                            <p className="text-[10px] text-brand-muted font-mono mt-1 opacity-70">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-widest ${user.role === 'admin' ? 'bg-brand-primary/10 text-brand-primary border border-brand-primary/20' : 'bg-brand-dark border border-brand-border text-brand-muted'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'ACTIVE' ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></div>
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${user.status === 'ACTIVE' ? 'text-emerald-500' : 'text-red-500'}`}>
                            {user.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className={`px-2 py-1 rounded text-[9px] font-bold uppercase border ${getKycBadge(user.kycStatus)}`}>
                          {user.kycStatus}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedUserId(user.id);
                              fetchDetail(user.id);
                            }}
                            className="p-2 rounded-lg bg-brand-dark border border-brand-border text-brand-muted hover:text-white hover:border-brand-primary/30 transition-all"
                            title="Inspect Entity"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(user.id, user.status)}
                            className={`p-2 rounded-lg border transition-all ${user.status === 'ACTIVE' ? 'bg-red-500/10 border-red-500/20 text-red-500 hover:bg-red-500/20' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500 hover:bg-emerald-500/20'}`}
                            title={user.status === 'ACTIVE' ? 'Suspend Terminal Access' : 'Activate Terminal Access'}
                          >
                            {user.status === 'ACTIVE' ? <UserX size={16} /> : <UserCheck size={16} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {users.map((user) => (
                <div key={user.id} className="bg-brand-card border border-brand-border rounded-2xl p-5 space-y-4 shadow-lg active:scale-[0.98] transition-transform">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-brand-dark border border-brand-border flex items-center justify-center text-brand-primary font-bold">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-sm tracking-tight">{user.name}</h4>
                        <span className="text-[9px] font-black text-brand-muted uppercase tracking-[0.2em]">{user.role}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleStatus(user.id, user.status)}
                      className={`p-2 rounded-xl border ${user.status === 'ACTIVE' ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'}`}
                    >
                      {user.status === 'ACTIVE' ? <UserX size={18} /> : <UserCheck size={18} />}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 py-3 border-y border-brand-border/30">
                    <div className="space-y-0.5">
                      <p className="text-[8px] text-brand-muted uppercase font-black tracking-widest">KYC Status</p>
                      <span className={`text-[10px] font-bold ${user.kycStatus === 'APPROVED' ? 'text-emerald-500' : 'text-brand-primary'}`}>{user.kycStatus}</span>
                    </div>
                    <div className="space-y-0.5 text-right">
                      <p className="text-[8px] text-brand-muted uppercase font-black tracking-widest">Access Status</p>
                      <span className={`text-[10px] font-bold ${user.status === 'ACTIVE' ? 'text-emerald-500' : 'text-red-500'}`}>{user.status}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-[9px] text-brand-muted font-mono">{user.email}</p>
                    <button
                      onClick={() => {
                        setSelectedUserId(user.id);
                        fetchDetail(user.id);
                      }}
                      className="text-brand-primary flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest"
                    >
                      Details <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-6 py-2 bg-brand-dark border border-brand-border rounded-xl text-xs font-black text-brand-muted hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                PREV
              </button>
              <span className="text-[10px] font-black text-white bg-brand-primary/10 border border-brand-primary/20 px-4 py-2 rounded-lg">PAGE {page}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={users.length < 10}
                className="px-6 py-2 bg-brand-dark border border-brand-border rounded-xl text-xs font-black text-brand-muted hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                NEXT
              </button>
            </div>
          </>
        )}
      </div>

      {/* 4. Entity Audit Modal */}
      {selectedUserId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-brand-dark/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-brand-card border border-brand-border w-full max-w-xl rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.8)] animate-in zoom-in-95 duration-300 relative">
            {isDetailLoading && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-brand-dark/60">
                <Loader2 className="animate-spin text-brand-primary" size={40} />
              </div>
            )}

            <div className="p-6 border-b border-brand-border flex items-center justify-between bg-brand-dark/40">
              <div>
                <h3 className="text-white font-bold uppercase tracking-tight truncate max-w-[300px]">Entity Audit Profile: {selectedUserId}</h3>
                <p className="text-[10px] text-brand-muted uppercase font-bold tracking-widest mt-0.5">Deep inspection of participant metadata</p>
              </div>
              <button onClick={() => { setSelectedUserId(null); setUserDetail(null); }} className="p-2 rounded-xl bg-brand-dark hover:bg-brand-border text-brand-muted hover:text-white transition-all">
                <X size={20} />
              </button>
            </div>

            {userDetail && (
              <div className="p-8 space-y-8 max-h-[80vh] overflow-y-auto">
                {/* Profile Overview */}
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-brand-dark border border-brand-border flex items-center justify-center text-2xl font-black text-brand-primary">
                    {userDetail.profile.name.charAt(0)}
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-2xl font-bold text-white tracking-tighter">{userDetail.profile.name}</h4>
                    <div className="flex items-center gap-2">
                      <Mail size={12} className="text-brand-muted" />
                      <p className="text-xs text-brand-muted font-mono">{userDetail.profile.email}</p>
                    </div>
                  </div>
                </div>

                {/* Security Section */}
                <div className="space-y-4 bg-brand-dark/50 p-6 rounded-2xl border border-brand-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-brand-muted">
                      <Lock size={14} />
                      <span className="text-[9px] font-black uppercase tracking-widest">Master Key Access</span>
                    </div>
                    <Button
                      onClick={() => handleShowPassword(selectedUserId)}
                      isLoading={isPasswordLoading}
                      className="h-8 px-4 text-[8px] tracking-[0.2em]"
                      variant="secondary"
                    >
                      {revealedPassword ? 'KEY REVEALED' : 'REQUEST DECRYPTION'}
                    </Button>
                  </div>
                  {revealedPassword ? (
                    <div className="bg-brand-primary/10 border border-brand-primary/30 p-4 rounded-xl text-center">
                      <p className="text-brand-primary font-mono font-bold tracking-[0.5em] text-lg">{revealedPassword}</p>
                    </div>
                  ) : (
                    <div className="bg-white/5 p-4 rounded-xl text-center">
                      <p className="text-white/20 font-mono font-bold tracking-[0.5em] text-lg">••••••••••••</p>
                    </div>
                  )}
                </div>

                {/* Compliance Ledger */}
                <div className="space-y-4">
                  <h5 className="text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Compliance Matrix</h5>
                  <div className="space-y-2">
                    <DetailItem label="Verification status" value={userDetail.kyc?.status || 'NOT SUBMITTED'} color={userDetail.kyc?.status === 'APPROVED' ? 'text-emerald-500' : 'text-brand-primary'} />
                    <DetailItem label="Verification level" value={userDetail.kyc?.verificationLevel || 'LEVEL 0'} />
                    <DetailItem label="Submission Date" value={userDetail.kyc?.submittedAt ? new Date(userDetail.kyc.submittedAt).toLocaleDateString() : 'N/A'} />
                    <DetailItem label="Terminal Access" value={userDetail.profile.status} color={userDetail.profile.status === 'ACTIVE' ? 'text-emerald-500' : 'text-red-500'} />
                    <DetailItem label="Global Authority" value={userDetail.profile.role} />
                    <DetailItem label="Last Uplink" value={userDetail.security?.lastLoginAt ? new Date(userDetail.security.lastLoginAt).toLocaleString() : 'NEVER'} />
                    <DetailItem label="Node Creation" value={new Date(userDetail.profile.createdAt).toLocaleDateString()} />
                  </div>
                </div>

                <div className="pt-4 flex gap-3 sticky bottom-0 bg-brand-card">
                  <Button onClick={() => { setSelectedUserId(null); setUserDetail(null); }} className="flex-1 h-12" variant="secondary">
                    CLOSE INSPECTION
                  </Button>
                  <Button
                    onClick={() => {
                      handleToggleStatus(selectedUserId, userDetail.profile.status);
                    }}
                    className="flex-1 h-12"
                    variant={userDetail.profile.status === 'ACTIVE' ? 'danger' : 'primary'}
                  >
                    {userDetail.profile.status === 'ACTIVE' ? 'SUSPEND TERMINAL' : 'ACTIVATE TERMINAL'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem: React.FC<{ label: string, value: string, color?: string }> = ({ label, value, color = 'text-white' }) => (
  <div className="flex items-center justify-between py-2 border-b border-brand-border/30 last:border-0">
    <span className="text-[11px] font-bold text-brand-muted uppercase tracking-widest">{label}</span>
    <span className={`text-[11px] font-black uppercase tracking-tighter ${color}`}>{value}</span>
  </div>
);

export default AdminUsers;
